import React from 'react';

import { useTextEditorStore } from './useTextEditorStore';

import { logger } from '@/utils/logger';
import { showErrorToast } from '@/utils/toast';

interface Props {
  children: React.ReactNode;
  onReset: () => void;
}

interface State {
  hasError: boolean;
}

export class TextEditorErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logger.error('TextEditorErrorBoundary caught error', { error, info });
    useTextEditorStore.getState().clearElements();
  }

  override render() {
    if (this.state.hasError) {
      return <TextEditorErrorFallback onReset={this.props.onReset} />;
    }
    return this.props.children;
  }
}

function TextEditorErrorFallback({ onReset }: { onReset: () => void }) {
  React.useEffect(() => {
    showErrorToast(
      'Text editor error',
      'The text editor hit a snag — your image was not modified.',
    );
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(onReset, 100);
    return () => clearTimeout(timer);
  }, [onReset]);

  return null;
}
